import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, Flame, MessageCircle, ChevronRight } from "lucide-react";

export default function Students() {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/admin/students").then(r => setRows(r.data));
  }, []);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 lg:p-12">
        <div className="label-caps mb-3">Instructor view</div>
        <h1 className="font-serif text-4xl lg:text-6xl leading-none mb-2">Student roster.</h1>
        <p className="text-muted-foreground mb-10">{rows.length} student{rows.length === 1 ? "" : "s"} enrolled.</p>

        {rows.length === 0 ? (
          <Card className="p-10 text-center">
            <div className="font-serif text-2xl">No students yet</div>
            <p className="text-sm text-muted-foreground mt-1">When students register, they'll appear here.</p>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="students-list">
            {rows.map(r => (
              <Link key={r.id} to={`/admin/students/${r.id}`} data-testid={`student-row-${r.id}`}>
                <Card className="p-5 hover:border-primary transition-colors cursor-pointer">
                  <div className="grid md:grid-cols-6 gap-4 items-center">
                    <div className="md:col-span-2 min-w-0">
                      <div className="font-serif text-xl truncate flex items-center gap-2">
                        {r.name}
                        {r.access_status === "active" ? (
                          <span className="label-caps text-[9px] px-1.5 py-0.5 rounded bg-primary/15 text-primary" data-testid={`paid-badge-${r.id}`}>Paid · {r.plan || "—"}</span>
                        ) : (
                          <span className="label-caps text-[9px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground" data-testid={`unpaid-badge-${r.id}`}>Unpaid</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{r.email}</div>
                    </div>
                    <div>
                      <div className="label-caps text-[10px]">Chapters</div>
                      <div className="font-mono text-sm">{r.chapters_passed} / 13</div>
                      <Progress value={(r.chapters_passed / 13) * 100} className="h-1 mt-1" />
                    </div>
                    <div>
                      <div className="label-caps text-[10px]">Avg quiz</div>
                      <div className="font-mono text-sm">{r.average_score}%</div>
                    </div>
                    <div>
                      <div className="label-caps text-[10px]">Final exam</div>
                      <div className="font-mono text-sm flex items-center gap-1">
                        {r.exam_score != null ? `${r.exam_score}%` : "—"}
                        {r.exam_passed && <CheckCircle2 size={14} className="text-primary" />}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-1 text-sm" title="Current streak">
                          <Flame size={14} className={r.current_streak > 0 ? "text-accent" : "text-muted-foreground"} />
                          <span className="font-mono">{r.current_streak}d</span>
                        </div>
                        {r.open_questions > 0 && (
                          <div className="flex items-center gap-1 text-sm text-accent mt-1" title="Open questions">
                            <MessageCircle size={14} />
                            <span className="font-mono">{r.open_questions} open</span>
                          </div>
                        )}
                        {r.last_activity && <div className="text-[10px] text-muted-foreground mt-1">Last: {r.last_activity}</div>}
                      </div>
                      <ChevronRight size={16} className="text-muted-foreground shrink-0" />
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
